import { supabase } from './supabaseClient'

export type DeletionScope = 'wellness_data' | 'account'

export type DeletionScopeDetails = {
  id: DeletionScope
  title: string
  summary: string
  bullets: string[]
  retentionNote?: string
  requiresSignOut: boolean
}

export const DELETION_SCOPES: Record<DeletionScope, DeletionScopeDetails> = {
  wellness_data: {
    id: 'wellness_data',
    title: 'Delete wellness data',
    summary:
      'Removes wearable metrics, training plans, meal logs, and AI coaching history. Your login and subscription stay active.',
    bullets: [
      'Wearable and health metrics (heart rate, sleep, activity)',
      'Training schedules, workout logs, and session feedback',
      'Meal protocols, grocery lists, and nutrition logs',
      'Calendar sync data and AI coaching recommendations',
      'Onboarding answers and health screening responses',
    ],
    requiresSignOut: false,
  },
  account: {
    id: 'account',
    title: 'Delete account & all data',
    summary:
      'Permanently deletes your Kilo account and all associated personal data. This cannot be undone.',
    bullets: [
      'Everything listed under “Delete wellness data”',
      'Your profile, email, and authentication credentials',
      'Device connections and coach preferences',
      'Active Stripe subscription (cancelled automatically when billing is linked)',
    ],
    retentionNote:
      'We retain a minimal anonymised record of this deletion request for regulatory compliance. Active Stripe billing is cancelled when linked. Invoice records may be kept where required by tax or accounting law.',
    requiresSignOut: true,
  },
}

export function isDeletionConfirmationValid(value: string): boolean {
  return value.trim().toUpperCase() === 'DELETE'
}

export type DeleteUserDataResponse = {
  ok: boolean
  scope?: DeletionScope
  requires_sign_out?: boolean
  error?: string
}

export async function requestDataDeletion(
  scope: DeletionScope,
): Promise<{ ok: boolean; requiresSignOut: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke<DeleteUserDataResponse>('delete-user-data', {
    body: { scope, confirmation: 'DELETE' },
  })

  if (error) {
    return { ok: false, requiresSignOut: false, error: error.message }
  }

  if (!data?.ok) {
    return { ok: false, requiresSignOut: false, error: data?.error ?? 'Deletion failed' }
  }

  return {
    ok: true,
    requiresSignOut: Boolean(data.requires_sign_out),
  }
}
